import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { ReadMore } from '../../../design-system/components/read-more/ReadMore';
import { Typography } from '../../../design-system/components/typography/Typography';
import { HtmlContent } from '../../../domains/Layout/HtmlContent/HtmlContent';
import { Destination } from '../../../resources/destination/domain/types/destination';
import { PointOfInterestListIntroduction } from '../../../resources/pointOfInterestListIntroduction/domain/types/PointOfInterestListIntroduction';
import { PickDomain } from '../../../shared/utilities/types/PickDomain';

import style from './PointOfInterestListHeader.module.css';

type PointOfInterestListHeaderProps = {
    destination: PickDomain<Destination, 'name' | 'preposition'>;
    introduction: PickDomain<PointOfInterestListIntroduction, 'content'> | null;
};

export function PointOfInterestListHeader({
    destination,
    introduction,
}: PointOfInterestListHeaderProps) {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();

    return (
        <div className={style.container}>
            <div className={style.titleWrapper}>
                <Typography className={style.subTitle} as="div" scale="primary-s-extra-bold">
                    {formatMessage({ id: 'global.component.breadcrumb.pointOfInterestList' })}
                </Typography>
                <Typography className={style.title} as="h1" scale="secondary-l-bold">
                    {formatMessage(
                        { id: 'page.poiList.subTitle' },
                        {
                            destinationPreposition: destination.preposition,
                            destinationName: destination.name,
                        },
                    )}
                </Typography>
            </div>

            {introduction ? (
                <div>
                    <ReadMore
                        lineClamp={4}
                        readMore={formatMessage({ id: 'global.cta.seeMore' })}
                        readLess={formatMessage({ id: 'global.cta.seeLess' })}
                        onToggle={(isOpen) => {
                            trackEvent((ampli, defaultProperties) => {
                                if (isOpen) {
                                    ampli.moreDetailsClicked({
                                        ...defaultProperties,
                                        block: 'description',
                                    });
                                }
                            });
                        }}
                    >
                        <HtmlContent htmlContent={introduction.content} />
                    </ReadMore>
                </div>
            ) : null}
        </div>
    );
}
